"use client";

import type { NotificationEvent } from "./notification-sounds";

/**
 * Preferencia de alerta sonoro, POR DEVICE (localStorage) — mesma semantica do
 * `chat-audio-preference.ts` que este modulo substitui: o atendente pode querer
 * som no desktop do plantao e nao no notebook de casa. Sem endpoint e sem
 * migration por um punhado de booleans.
 */
export interface SoundPreferences {
  conversaAtribuida: boolean;
  conversaTransferida: boolean;
  mensagemNova: boolean;
  mencao: boolean;
  alertaCritico: boolean;
  /** Entrada e saida compartilham um switch — sao o mesmo par no card. */
  entradaSaida: boolean;
  /** true = so toca com a aba em segundo plano. Paridade fork: playAlertOnlyWhenHidden. */
  onlyWhenHidden: boolean;
}

const STORAGE_KEY = "notifications:sound";
/** Chave da versao anterior (1 boolean, so atribuicao). Migrada e removida. */
const LEGACY_KEY = "chat:audioAlertOnAssign";

export const DEFAULT_SOUND_PREFERENCES: SoundPreferences = {
  conversaAtribuida: true,
  conversaTransferida: true,
  mensagemNova: true,
  mencao: true,
  alertaCritico: true,
  entradaSaida: true,
  // Default false: o atendente quer ouvir a transferencia mesmo olhando a tela.
  // As travas de contexto (nao tocar na conversa aberta) ja evitam o ruido.
  onlyWhenHidden: false,
};

/** Todos os eventos desligados — destino da migracao de quem ja tinha silenciado. */
const ALL_MUTED: SoundPreferences = {
  conversaAtribuida: false,
  conversaTransferida: false,
  mensagemNova: false,
  mencao: false,
  alertaCritico: false,
  entradaSaida: false,
  onlyWhenHidden: false,
};

export function readSoundPreferences(): SoundPreferences {
  if (typeof window === "undefined") return DEFAULT_SOUND_PREFERENCES;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      // Merge com o default: preferencia gravada por versao antiga pode nao ter
      // todos os campos, e campo ausente deve assumir o default (nao `false`).
      return { ...DEFAULT_SOUND_PREFERENCES, ...(JSON.parse(raw) as object) };
    }

    // Sem preferencia nova: migra a chave antiga uma unica vez. Quem tinha
    // silenciado o beep de atribuicao nao pode ser surpreendido com 5 sons.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) {
      const migrated = legacy === "false" ? ALL_MUTED : DEFAULT_SOUND_PREFERENCES;
      writeSoundPreferences(migrated);
      // Consome a chave antiga: se ficasse, uma escolha futura do user seria
      // reinterpretada como migracao a cada leitura sem a chave nova.
      localStorage.removeItem(LEGACY_KEY);
      return migrated;
    }
  } catch {
    /* localStorage indisponivel ou JSON corrompido — cai no default */
  }

  return DEFAULT_SOUND_PREFERENCES;
}

export function writeSoundPreferences(prefs: SoundPreferences): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* modo privado em alguns browsers — ignora */
  }
}

/** Traduz o evento para a chave de preferencia que o governa. */
export function isEventEnabled(
  prefs: SoundPreferences,
  event: NotificationEvent,
): boolean {
  if (event === "entrada" || event === "saida") return prefs.entradaSaida;
  return prefs[event];
}
